import { fetchWithAuthRelogin } from '../utils/sessionRelogin';
import { getMiniappAccessToken } from '../utils/miniappAccessToken';

function getSupportBase() {
  if (typeof window === 'undefined') return '';
  const origin = window.location.origin;
  const path = process.env.REACT_APP_SUPPORT_API_PATH || '/api/support';
  return origin + path;
}

function authHeaders(extra = {}) {
  const token = getMiniappAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

async function parseOrThrow(res, fallbackPrefix) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg =
      typeof data.detail === 'string'
        ? data.detail
        : data.detail?.message || `${fallbackPrefix}: ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export async function sendSupportChatMessage(payload) {
  const base = getSupportBase();
  const res = await fetchWithAuthRelogin(`${base}/chat/message`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload || {}),
  });
  return parseOrThrow(res, 'chat-message');
}

export async function sendSupportChatMessageWithFiles({ ticket_id, text, username, files }) {
  const base = getSupportBase();
  const form = new FormData();
  if (ticket_id) form.append('ticket_id', String(ticket_id));
  if (typeof text === 'string') form.append('text', text);
  if (typeof username === 'string' && username.trim()) form.append('username', username.trim());
  const list = Array.isArray(files) ? files.slice(0, 3) : [];
  list.forEach((f) => form.append('files', f));
  const res = await fetchWithAuthRelogin(`${base}/chat/message/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  });
  return parseOrThrow(res, 'chat-message-upload');
}

export async function getSupportChatTickets({ includeArchived = true, archivedOnly = false } = {}) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('include_archived', includeArchived ? 'true' : 'false');
  qs.set('archived_only', archivedOnly ? 'true' : 'false');
  const res = await fetchWithAuthRelogin(`${base}/chat/tickets?${qs.toString()}`, {
    headers: authHeaders(),
  });
  return parseOrThrow(res, 'chat-tickets');
}

export async function getSupportArchivedTickets() {
  const base = getSupportBase();
  const res = await fetchWithAuthRelogin(`${base}/chat/tickets/archive`, {
    headers: authHeaders(),
  });
  return parseOrThrow(res, 'chat-archive');
}

export async function getSupportChatMessages(ticketId, afterId = 0) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('after_id', String(afterId || 0));
  const res = await fetchWithAuthRelogin(
    `${base}/chat/tickets/${encodeURIComponent(ticketId)}/messages?${qs.toString()}`,
    { headers: authHeaders() }
  );
  return parseOrThrow(res, 'chat-messages');
}

export async function pollSupportChat(ticketId, afterId = 0, timeoutSec = 25) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('after_id', String(afterId || 0));
  qs.set('timeout_sec', String(timeoutSec));
  const res = await fetchWithAuthRelogin(`${base}/chat/tickets/${encodeURIComponent(ticketId)}/poll?${qs.toString()}`, {
    headers: authHeaders(),
  });
  return parseOrThrow(res, 'chat-poll');
}

export async function selectSupportFaq(ticketId, faqId, afterId = 0) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('after_id', String(afterId || 0));
  const res = await fetchWithAuthRelogin(
    `${base}/chat/tickets/${encodeURIComponent(ticketId)}/bot/select-faq?${qs.toString()}`,
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ faq_id: faqId }),
    }
  );
  return parseOrThrow(res, 'chat-faq-select');
}

export async function escalateSupportTicket(ticketId, afterId = 0) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('after_id', String(afterId || 0));
  const res = await fetchWithAuthRelogin(
    `${base}/chat/tickets/${encodeURIComponent(ticketId)}/bot/escalate?${qs.toString()}`,
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({}),
    }
  );
  return parseOrThrow(res, 'chat-escalate');
}

export async function sendSupportPrecloseDecision(ticketId, continueChat, afterId = 0) {
  const base = getSupportBase();
  const qs = new URLSearchParams();
  qs.set('after_id', String(afterId || 0));
  const res = await fetchWithAuthRelogin(
    `${base}/chat/tickets/${encodeURIComponent(ticketId)}/preclose/decision?${qs.toString()}`,
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ continue_chat: Boolean(continueChat) }),
    }
  );
  return parseOrThrow(res, 'chat-preclose-decision');
}

export async function getSupportFaqById(faqId) {
  const base = getSupportBase();
  const res = await fetchWithAuthRelogin(`${base}/faq/${encodeURIComponent(faqId)}`, {
    headers: authHeaders(),
  });
  return parseOrThrow(res, 'faq-by-id');
}

export function getSupportChatMediaUrl(mediaId) {
  const base = getSupportBase();
  return `${base}/chat/media/${encodeURIComponent(mediaId)}`;
}

export async function fetchSupportChatMediaBlob(mediaId) {
  const url = getSupportChatMediaUrl(mediaId);
  const res = await fetchWithAuthRelogin(url, { headers: authHeaders() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg =
      typeof data.detail === 'string'
        ? data.detail
        : data.detail?.message || `chat-media-fetch: ${res.status}`;
    throw new Error(msg);
  }
  return res.blob();
}

